import ownAuthSession from './auth/ownAuthSession';

// Süper admin › Kasa Hataları: kasada "Ödeme Kaydı Hatası" ekranının her çıkışı bir satır.
// Sunucu, satışın gerçekten kaydedilip kaydedilmediğini kendisi kontrol eder (serverCheck).
// Uçlar gts_dotnetapi'de: /api/superadmin/pos-errors ([SuperAdminOnly]).

const NETAPI_URL = process.env.REACT_APP_NETAPI_URL || 'https://gtsnetapi.aquasoft.com.tr';

export type PosErrorRange = 'today' | 'yesterday' | '7d' | '30d';
export type PosErrorTab = 'lost' | 'all';

export type PosScreenMode = 'restoran' | 'hizli-satis' | 'paket';
export type PosFiscalDevice = 'pavo_rest_p2p' | 'pavo_p2p' | 'pavopay' | 'beko' | 'ingenico' | 'inpos' | 'hugin' | 'yok';
export type PosErrorKind = 'zaman-asimi' | 'ag' | 'sunucu' | 'istemci';
export type PosErrorResult =
  | 'kayip-suphesi'
  | 'sunucuda-kayitli'
  | 'yeniden-denemede-kaydedildi'
  | 'para-alinmadi'
  | 'kontrol-ediliyor'
  | 'bilinmiyor';

/** 0 açık, 1 yeniden denemede kaydedildi, 2 kasiyer kapattı, 3 sayfa açılınca kurtarıldı */
export type PosClientOutcome = 0 | 1 | 2 | 3;
/** 0 kontrol edilmedi, 1 sunucuda kayıtlı, 2 sunucuda yok, 3 kontrol edilemiyor */
export type PosServerCheck = 0 | 1 | 2 | 3;

export interface PosErrorRow {
  id: number;
  occurredAt: string;
  companyId: string | null;
  companyName: string | null;
  branchId: string | null;
  branchName: string | null;
  screenId: string | null;
  screenMode: PosScreenMode | null;
  tableName: string | null;
  cashierName: string | null;
  paymentKind: string | null;
  fiscalDevice: PosFiscalDevice | null;
  fiscalDone: boolean | null;
  fiscalReference: string | null;
  amount: number | null;
  endpoint: string | null;
  idempotencyKey: string | null;
  pavoRequestId: string | null;
  errorKind: PosErrorKind | null;
  httpStatus: number | null;
  errorMessage: string | null;
  durationMs: number | null;
  attempt: number | null;
  clientOutcome: PosClientOutcome | null;
  serverCheck: PosServerCheck | null;
  serverReference: string | null;
  checkedAt: string | null;
  result: PosErrorResult | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  reviewNote: string | null;
  appVersion: string | null;
  userAgent: string | null;
}

export interface PosErrorSummary {
  lostSuspicion: number;
  total: number;
  savedOnServer: number;
  retriedOk: number;
  companies: number;
  generatedAt: string;
}

export interface PosErrorCause {
  label: string;
  count: number;
}

export interface PosErrorListFilters {
  tab: PosErrorTab;
  range: PosErrorRange;
  companyId?: string;
  fiscalDevice?: string;
  errorKind?: string;
  screenMode?: string;
  hideReviewed?: boolean;
  skip?: number;
  take?: number;
}

async function posErrorsRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await ownAuthSession.getAccessToken();
  if (!token) throw new Error('Oturum açılmamış');

  const response = await fetch(`${NETAPI_URL}/api/superadmin/pos-errors${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 404 && !(data as { message?: string }).message) {
      throw new Error('Ana API bu ucu tanımıyor; gts_dotnetapi yeni sürümle yayınlanmalı.');
    }
    throw new Error((data as { message?: string }).message || 'Kasa hataları isteği başarısız');
  }
  return data as T;
}

export function getPosErrorSummary(range: PosErrorRange) {
  return posErrorsRequest<PosErrorSummary>(`/summary?range=${encodeURIComponent(range)}`);
}

export function getPosErrors(filters: PosErrorListFilters) {
  const params = new URLSearchParams();
  params.set('tab', filters.tab);
  params.set('range', filters.range);
  if (filters.companyId) params.set('companyId', filters.companyId);
  if (filters.fiscalDevice) params.set('fiscalDevice', filters.fiscalDevice);
  if (filters.errorKind) params.set('errorKind', filters.errorKind);
  if (filters.screenMode) params.set('screenMode', filters.screenMode);
  params.set('hideReviewed', filters.hideReviewed ? 'true' : 'false');
  params.set('skip', String(filters.skip ?? 0));
  params.set('take', String(filters.take ?? 25));
  return posErrorsRequest<{ items: PosErrorRow[]; total: number }>(`?${params.toString()}`);
}

export function getPosError(id: number) {
  return posErrorsRequest<PosErrorRow>(`/${id}`);
}

export function reviewPosError(id: number, note: string) {
  return posErrorsRequest<{ ok: boolean }>(`/${id}/review`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

export function getPosErrorCauses(range: PosErrorRange) {
  return posErrorsRequest<{ items: PosErrorCause[] }>(`/causes?range=${encodeURIComponent(range)}`);
}

export function getPosErrorBadge() {
  return posErrorsRequest<{ lostSuspicion: number }>('/badge');
}
